import { useSearchParams } from "react-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { RoomType } from "@prisma/client";
import { ModalContainer, ModalHeader, ModalBody, ModalFooter } from "@app/components/Modal";
// import UserSearch from "@app/components/UserSearch";
import PdfIcon from "@app/components/PdfIcon";
import useUser from "@app/zustand/user";
import { PictureUploadResponse, UserConnexionResponse } from "@api/src/types/responses";
import UserSearch from "@app/components/UserSearch";
import { UserSearched } from "@api/src/types/user";
import { CameraIcon, DocumentIcon, PhotoIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { Profession } from "@prisma/client";

type Attachment = {
  url: string;
  type: "image" | "video" | "pdf";
};

export default function FeedNewPost({
  open,
  setOpen,
  onNewPost,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onNewPost: () => void;
}) {
  const me = useUser((state) => state.user!);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [anonymous, setAnonymous] = useState(false);

  useEffect(() => {
    setAttachments([]);
  }, [open]);

  const [quotedUsers, setQuotedUsers] = useState<Array<UserSearched>>([]);

  const [showPoll, setShowPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(["", ""]);

  const addPollOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions([...pollOptions, ""]);
    }
  };

  const updatePollOption = (index: number, value: string) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      const newOptions = pollOptions.filter((_, i) => i !== index);
      setPollOptions(newOptions);
    }
  };

  const roomType = searchParams.get("qRoomType") as RoomType;
  const qFavs = searchParams.get("qFavs");

  const title = useMemo(() => {
    if (showPoll) {
      return "Nouveau sondage";
    }
    switch (roomType) {
      case RoomType.ETABLISSEMENT:
        return "Article - Sondage - Expérience - Recherche - Formation - Événement";
      case RoomType.DIRECT:
        return "Nouveau message";
      case RoomType.STATUS_PRO:
      case RoomType.SPECIALITE:
      default:
        return "Un article, une expérience, une question\u00A0?";
    }
  }, [roomType, showPoll]);

  const placeholder = useMemo(() => {
    if (showPoll) {
      return "Question du sondage...";
    }
    switch (roomType) {
      case RoomType.STATUS_PRO:
        if (me.status_pro === "Interne") {
          return "Quand est le prochain cours avec M. X ?\nOù sont les clés de la salle de garde ?\nCommencez à écrire ici...";
        } else {
          return "Commencez à écrire ici...";
        }
      case RoomType.SPECIALITE:
      case RoomType.ETABLISSEMENT:
      default:
        return "Commencez à écrire ici...";
    }
  }, [me.status_pro, roomType, showPoll]);

  const [showUserSearch, setShowUserSearch] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    window.sessionStorage.setItem("newPostContent", e.currentTarget.value);

    const cursorPos = e.target.selectionStart;
    const content = e.target.value;

    // Check if @ was just typed
    if (content[cursorPos - 1] === "@") {
      setShowUserSearch(true);
    }
  };

  function displayQuotedUser(user: UserSearched) {
    return `@${user.firstName} ${user.lastName} `;
  }

  const handleUserSelect = (user: UserSearched) => {
    if (!textareaRef.current) return;

    const content = textareaRef.current.value;
    const cursorPos = textareaRef.current.selectionStart;
    const newContent = content.slice(0, cursorPos - 1) + displayQuotedUser(user) + content.slice(cursorPos);

    textareaRef.current.value = newContent;
    window.sessionStorage.setItem("newPostContent", newContent);
    setShowUserSearch(false);
    setQuotedUsers((prev) => [...prev, user]);
    textareaRef.current.focus();
  };

  if (qFavs) {
    return null;
  }

  return (
    <ModalContainer open={open} className="flex-col" align="center">
      <ModalHeader title={title} onClose={() => setOpen(false)} />
      <ModalBody>
        <form
          id="newpostform"
          method="POST"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setLoading(true);
            const content = e.currentTarget.content.value;
            let roomId = searchParams.get("qRoomId");
            const roomType = searchParams.get("qRoomType");
            // en ce moment, on n'a qu'un seul feed: celui de l'établissement
            // si c'est un médecin qui poste, on poste en fait dans le feed de sa spécialité,
            // et dans le backend, si le médecin ne veut voir que les posts de sa spécialité, c'est plus simple
            // si c'est un infirmier qui poste, on poste dans le feed de son établissement directos
            if (me.profession === Profession.MEDECIN && roomType === RoomType.ETABLISSEMENT) {
              roomId = me.rooms.find((r) => r.room.roomType === RoomType.SPECIALITE)!.room.id;
            }
            fetch(`${import.meta.env.VITE_API_URL}/post`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                content,
                quotedUsers: quotedUsers
                  .filter((u) => content.includes(displayQuotedUser(u)))
                  .map((u) => u.id)
                  .join(","),
                roomId: roomId,
                anonymous,
                profession: me.profession,
                attachments: attachments.map((a) => a.url),
                poll: showPoll
                  ? {
                      option1: pollOptions[0],
                      option2: pollOptions[1],
                      option3: pollOptions[2] || null,
                      option4: pollOptions[3] || null,
                      option5: pollOptions[4] || null,
                    }
                  : null,
              }),
            })
              .then((response) => response.json() as Promise<UserConnexionResponse>)
              .then((response) => {
                if (response.ok) {
                  onNewPost();
                  setAttachments([]);
                  window.sessionStorage.removeItem("newPostContent");
                } else if (response.error) {
                  alert(response.error);
                }
              })
              .finally(() => {
                setLoading(false);
              });
          }}
        />
        <div className="flex flex-col items-stretch md:py-2 relative">
          <textarea
            ref={textareaRef}
            rows={showPoll ? 1 : 5}
            form="newpostform"
            autoFocus
            name="content"
            placeholder={placeholder}
            className="mx-4 max-h-[50vh] rounded border border-sky-800/20 p-2 md:max-h-60"
            defaultValue={window.sessionStorage.getItem("newPostContent") ?? ""}
            onChange={handleTextareaChange}
          />

          <div className="mx-4 flex items-center justify-between">
            <ImageButton attachments={attachments} setAttachments={setAttachments} className="" />
            <button
              type="button"
              onClick={() => setShowPoll(true)}
              className={[
                "hover:underline text-xs text-sky-800 underline opacity-60",
                showPoll ? "invisible" : "",
              ].join(" ")}
            >
              Ajouter un sondage
            </button>
          </div>
          {showPoll && (
            <div className="space-y-2 mx-4 mt-4">
              {pollOptions.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updatePollOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 rounded border border-sky-800/20 p-2"
                  />
                  {index >= 2 && (
                    <button
                      type="button"
                      onClick={() => removePollOption(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              {pollOptions.length < 5 && (
                <button
                  type="button"
                  onClick={addPollOption}
                  className="text-sky-800 text-sm hover:underline"
                >
                  + Ajouter une option
                </button>
              )}
            </div>
          )}
          {showUserSearch && (
            <div className="absolute left-6 top-1/3 z-20 w-72 rounded-lg border border-sky-100 bg-black shadow-lg">
              <UserSearch
                onSave={handleUserSelect}
                onClose={() => {
                  setShowUserSearch(false);
                  textareaRef.current?.focus();
                }}
                quotedUsers={quotedUsers}
              />
            </div>
          )}
          {attachments.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2 mx-4 mt-2">
              {attachments.map((attachment, index) => (
                <div key={index} className="relative">
                  {attachment.type === "pdf" ? (
                    <div className="flex items-center gap-2 rounded-lg border p-2 size-20">
                      <PdfIcon className="h-4 w-4" />
                      <span className="text-xs truncate">PDF</span>
                    </div>
                  ) : attachment.type === "video" ? (
                    <video src={attachment.url} className="size-20 object-cover rounded-lg" />
                  ) : (
                    <img src={attachment.url} alt="" className="size-20 object-cover rounded-lg" />
                  )}
                  <button
                    type="button"
                    onClick={() => setAttachments((prev: Attachment[]) => prev.filter((_, i) => i !== index))}
                    className="absolute -top-2 -right-2 bg-gray-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-gray-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <label className="inline-block rounded-lg py-1 text-sky-800 mr-auto">
          <input
            type="checkbox"
            name="anonymous"
            className="mr-2"
            checked={anonymous}
            onChange={() => setAnonymous(!anonymous)}
          />
          Post anonyme
        </label>
        <button
          type="button"
          className="inline-block rounded-lg border border-sky-800 bg-white px-5 py-1 text-sky-800"
          onClick={() => setOpen(false)}
        >
          Annuler
        </button>
        <button
          type="submit"
          form="newpostform"
          className="inline-block rounded-lg border-none bg-sky-800 px-5 py-1 text-white hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "En cours..." : "Envoyer"}
        </button>
      </ModalFooter>
    </ModalContainer>
  );
}

function ImageButton({
  attachments,
  setAttachments,
  className,
}: {
  attachments: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  className?: string;
}) {
  const [savingImage, setSavingImage] = useState(false);
  const me = useUser((state) => state.user!);
  const uploadPicRef = useRef<HTMLButtonElement>(null);

  const getFileType = (file: File): "image" | "video" | "pdf" => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type === "application/pdf") return "pdf";
    return "image"; // fallback
  };

  return (
    <form
      method="post"
      encType="multipart/form-data"
      key={attachments.length}
      className={`relative ${className}`}
      onSubmit={async (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const file = formData.get("picture") as File;
        console.log("file", file);
        if (!file || file.size === 0) {
          return;
        }

        formData.append("pictureName", `${me.plausible_id}-post-${Date.now()}`);

        setSavingImage(true);
        await fetch(`${import.meta.env.VITE_API_URL}/picture`, {
          method: "POST",
          credentials: "include",
          body: formData,
        })
          .then((response) => response.json() as Promise<PictureUploadResponse>)
          .then((response) => {
            console.log("response", response);
            if (response.ok) {
              setAttachments((prev: Attachment[]) => [
                ...prev,
                {
                  url: response.data.url,
                  type: getFileType(file),
                },
              ]);
            } else if (response.error) {
              alert(response.error);
            }
          })
          .finally(() => {
            setSavingImage(false);
          });
      }}
    >
      <label
        htmlFor="picture"
        className="cursor-pointer text-xs text-sky-800 underline opacity-60 inline-flex gap-x-2"
      >
        {savingImage ? (
          "Enregistrement en cours..."
        ) : (
          <>
            <DocumentIcon className="size-4" />
            <CameraIcon className="size-4" />
            <VideoCameraIcon className="size-4" />
            <PhotoIcon className="size-4" />
          </>
        )}
      </label>
      <input
        type="file"
        key={attachments.length}
        disabled={savingImage}
        multiple
        onChange={(e) => {
          const files = e.currentTarget.files;
          if (!files) return;

          for (const file of files) {
            const formData = new FormData();
            formData.append("picture", file);
            formData.append("pictureName", `${me.plausible_id}-post-${Date.now()}`);

            setSavingImage(true);
            fetch(`${import.meta.env.VITE_API_URL}/picture`, {
              method: "POST",
              credentials: "include",
              body: formData,
            })
              .then((response) => response.json() as Promise<PictureUploadResponse>)
              .then((response) => {
                if (response.ok) {
                  setAttachments((prev) => [
                    ...prev,
                    {
                      url: response.data.url,
                      type: getFileType(file),
                    },
                  ]);
                }
              })
              .finally(() => setSavingImage(false));
          }
        }}
        name="picture"
        id="picture"
        className="hidden"
        accept="image/*,application/pdf,video/*"
      />
      <button ref={uploadPicRef} type="submit" value="picture" className="hidden">
        Ajouter une image, une vidéo, un PDF
      </button>
    </form>
  );
}
